import { setPublishingContracts, formatKey, type PlatformContract, platformById } from "@ondit/publishing";
import { post, request } from "~/core/api";
import type { PageVariant } from "~/core/api/index";

export type { PageVariant };
export interface Channel {
	platform: string;
	/** One of the platform's formats; empty or absent is its default. */
	format?: string;
	credential_id: string | null;
	target_url: string;
	fields: Record<string, string>;
}
/** What a distribution node posts: its channels and its picture. */
export interface Policy {
	channels: Channel[];
	/** A post gets a generated picture when this names an image model. */
	image_model: string;
	preset: string;
}
export interface Work {
	allowed: boolean;
	id: string;
	page_id: string;
	page_revision: number;
	config: Policy;
	run_id: string;
	current_revision: number;
	/** Where the promoted page lives, for the source line a post carries. */
	page: { kind: string; slug: string; locale: string };
	status: string;
	reason: string | null;
	/** The published page the posts promote. */
	title: string;
	spent: number;
	held: number;
	created_at: string;
	outcome: string;
	evidence: string;
	/** The page revision's platform versions: the posts. */
	variants: PageVariant[];
}
export interface Overview {
	work: Work[];
	decisions: { id: number; title: string; reason: string; created_at: string }[];
	platforms?: PlatformContract[];
	credentials?: { id: string; provider_id: string; name: string; account: string | null }[];
}
export const marketingKey = (brand: string) => ["marketing", brand];
const base = (brand: string) => `/agent/distribution/${brand}`;
export const overview = (brand: string) =>
	request<Overview>(`${base(brand)}/automation`).then((data) => {
		if (data.platforms) setPublishingContracts(data.platforms);
		return data;
	});
export const workCommand = (brand: string, work: string, body: unknown) => post(`${base(brand)}/work/${work}`, body);
export function workState(work: Work): string {
	if (work.status === "cancelled") return "cancelled";
	if (work.variants.some((v) => v.state === "unknown")) return "unknown";
	if (
		work.variants.length === work.config.channels.length &&
		work.variants.length > 0 &&
		work.variants.every((v) => v.state === "published")
	)
		return "published";
	if (work.variants.some((v) => v.state === "failed")) return "failed";
	if (work.variants.some((v) => v.state === "sending")) return "sending";
	if (work.variants.some((v) => v.state === "remote_draft")) return "remote_draft";
	return work.status;
}

/**
 * Whether `v` is the channel's version: same platform, same kind of post. A
 * channel saved before its platform had formats means the default, which is
 * what the server stored the version under.
 */
export function holds(c: Channel, v: Pick<PageVariant, "platform" | "format">): boolean {
	const p = platformById(c.platform);
	return v.platform === c.platform && (v.format ?? "") === (p ? formatKey(p, c.format) : (c.format ?? ""));
}
